import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { TestId } from '@repo/test-id';

import ExitModal from '@features/reservation/components/exit/ExitModal';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  vehicleChangePossible: boolean;
  isResolving: boolean;
  reservationId: string | undefined;
  onChangeVehicle: () => void;
}

// Navigating to these paths is part of the flow itself and must not trip the
// exit guard: staying on the flow, moving forward to the payment gateway, or
// ending it from the summary (a reservation the free parking time pays for is
// confirmed there, with no payment step).
const ALLOWED_FLOW_PATHS = [
  ROUTE_PATHS[RouteName.RESERVATION_FLOW],
  ROUTE_PATHS[RouteName.RESERVATION_PAYMENT],
  ROUTE_PATHS[RouteName.RESERVATION_SUCCESS],
  ROUTE_PATHS[RouteName.RESERVATION_FAILURE],
];

export default function ExitOverlay({ vehicleChangePossible, isResolving, reservationId, onChangeVehicle }: Props) {
  const testId = TestId.root('reservation.exitOverlay');

  // Guard the flow whenever there's a reservation worth protecting. Stay out of
  // the way while the flow is still resolving: a CREATE entry that finds an
  // existing pending reservation refuses and redirects to it, and that redirect
  // must win over the guard rather than trip the exit modal.
  const shouldGuard = reservationId !== undefined && !isResolving;

  const blocker = useBlocker(({ nextLocation }) => {
    if (!shouldGuard) return false;
    return !ALLOWED_FLOW_PATHS.includes(nextLocation.pathname);
  });

  useEffect(() => {
    if (!shouldGuard) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldGuard]);

  if (blocker.state !== 'blocked') return null;

  const handleChangeVehicle = () => {
    blocker.reset();
    onChangeVehicle();
  };
  const handleAbandon = async () => {
    if (!reservationId) return;
    blocker.proceed();
  };
  const handleDismiss = () => blocker.reset();

  return (
    <ExitModal
      testId={testId}
      vehicleChangePossible={vehicleChangePossible}
      onChangeVehicle={handleChangeVehicle}
      onAbandon={handleAbandon}
      onDismiss={handleDismiss}
    />
  );
}
